<template>
    <div class="stp">
        <div v-if="loading" class="stp-loading">
            <b-spinner small variant="secondary" />
            <span>scope 트리 로딩 중…</span>
        </div>

        <template v-else-if="tree">
            <div class="stp-header">
                <div class="stp-legend">
                    <span v-for="(cnt, role) in tree.roleCounts" :key="role" class="stp-legend-item">
                        <span class="stp-dot" :class="`stp-dot--${role}`"></span>
                        {{ roleMeta(role).label }}
                        <strong class="stp-legend-cnt">{{ cnt }}</strong>
                    </span>
                </div>

                <div v-if="hasEnergyEquip" class="stp-seg">
                    <button
                        type="button"
                        class="stp-seg-btn"
                        :class="{ 'stp-seg-btn--active': !detailMode }"
                        @click="detailMode = false"
                    >
                        요약
                    </button>
                    <button
                        type="button"
                        class="stp-seg-btn"
                        :class="{ 'stp-seg-btn--active': detailMode }"
                        @click="detailMode = true"
                    >
                        설비 펼침
                    </button>
                </div>
            </div>

            <div v-if="householdCount === 0" class="stp-warning">
                <i class="fa fa-exclamation-triangle"></i>
                <span>이 scope에는 세대 노드가 없습니다. 정산 대상 세대가 0건이 됩니다.</span>
            </div>

            <div v-if="displayNodes.length" class="stp-tree">
                <template v-for="node in displayNodes">
                    <div
                        v-if="node.role !== 'ENERGY_EQUIP' || detailMode"
                        :key="node.sysNodeIdx"
                        class="stp-row"
                        :class="`stp-row--${node.role}`"
                        :style="{ paddingLeft: rowIndent(node) }"
                    >
                        <span class="stp-dot stp-row-dot" :class="`stp-dot--${node.role}`"></span>

                        <span class="stp-row-name">{{ displayName(node) }}</span>

                        <span
                            v-if="showRolePill(node.role)"
                            class="stp-role-pill"
                            :class="`stp-role-pill--${node.role}`"
                            >{{ roleMeta(node.role).label }}</span
                        >

                        <span v-if="node.role === 'BUILDING'" class="stp-spacer"></span>
                        <span v-if="node.role === 'BUILDING'" class="stp-branch-summary">
                            세대 {{ buildingHhCount(node) }} · 설비 {{ buildingEqCount(node) }}
                        </span>

                        <span v-if="showInlineChips(node)" class="stp-chips">
                            <span
                                v-for="chip in equipByParent[node.sysNodeIdx]"
                                :key="chip.idx"
                                class="stp-chip"
                                :style="{ background: chip.soft, color: chip.ink }"
                            >
                                <span class="stp-chip-dot" :style="{ background: chip.dot }"></span>
                                {{ chip.label }}
                            </span>
                        </span>
                    </div>
                </template>
            </div>

            <div v-else class="stp-empty">표시할 정산 계통 노드가 없습니다.</div>
        </template>

        <div v-else class="stp-empty">scope를 선택하면 계통 트리 미리보기가 표시됩니다.</div>
    </div>
</template>

<script>
import { ROLE_META } from "../utils/settlementFormat.js";

const ROLE_ORDER = {
    SITE_ROOT: 0,
    BUILDING: 1,
    COMMON_AREA: 2,
    HOUSEHOLD: 3,
    ENERGY_EQUIP: 4,
};

function parseEnergyLabel(name = "") {
    if (name.includes("가스")) return "가스";
    if (name.includes("전기")) return "전기";
    if (name.includes("수도")) return "수도";
    const last = name.split("-").pop().trim();
    return last || name;
}

const ENERGY_PALETTE = {
    전기: { dot: "#b45309", soft: "#faf3e3", ink: "#92500a" },
    가스: { dot: "#b91c1c", soft: "#fbeceb", ink: "#991b1b" },
    수도: { dot: "#1d4ed8", soft: "#eaf0fd", ink: "#1e40af" },
};
const ENERGY_FALLBACK = { dot: "#9aa3b2", soft: "#eef0f5", ink: "#5b6472" };

export default {
    name: "ScopeTreePreview",
    props: {
        tree: { type: Object, default: null },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {
            detailMode: false,
        };
    },
    computed: {
        householdCount() {
            return this.tree?.roleCounts?.HOUSEHOLD || 0;
        },
        hasEnergyEquip() {
            return (this.tree?.roleCounts?.ENERGY_EQUIP || 0) > 0;
        },
        visibleNodes() {
            return (this.tree?.nodes || []).filter((n) => n.role && n.role !== "IGNORE");
        },
        treeRoots() {
            return this.buildTree(this.visibleNodes);
        },
        displayNodes() {
            return this.flattenTree(this.treeRoots);
        },
        equipByParent() {
            const map = {};
            for (const node of this.visibleNodes) {
                if (node.role !== "ENERGY_EQUIP") continue;
                const parentIdx = node.parentIdx != null ? Number(node.parentIdx) : null;
                if (parentIdx == null) continue;
                if (!map[parentIdx]) map[parentIdx] = [];
                const label = parseEnergyLabel(node.sysNodeName);
                const colors = ENERGY_PALETTE[label] || ENERGY_FALLBACK;
                map[parentIdx].push({ idx: node.sysNodeIdx, label, ...colors });
            }
            return map;
        },
    },
    methods: {
        roleMeta(role) {
            return ROLE_META[role] || ROLE_META.IGNORE;
        },
        rowIndent(node) {
            return 8 + node.displayDepth * 18 + "px";
        },
        displayName(node) {
            if (node.role === "ENERGY_EQUIP") return parseEnergyLabel(node.sysNodeName);
            return node.sysNodeName;
        },
        showRolePill(role) {
            return role === "SITE_ROOT" || role === "BUILDING" || role === "COMMON_AREA";
        },
        showInlineChips(node) {
            if (this.detailMode) return false;
            if (node.role !== "HOUSEHOLD" && node.role !== "COMMON_AREA") return false;
            return !!this.equipByParent[node.sysNodeIdx]?.length;
        },
        buildingHhCount(node) {
            return (node.children || []).filter((c) => c.role === "HOUSEHOLD").length;
        },
        buildingEqCount(node) {
            let count = 0;
            for (const child of node.children || []) {
                if (child.role === "HOUSEHOLD") {
                    count += (child.children || []).filter((c) => c.role === "ENERGY_EQUIP").length;
                } else if (child.role === "COMMON_AREA") {
                    count += (child.children || []).filter((c) => c.role === "ENERGY_EQUIP").length;
                }
            }
            return count;
        },
        buildTree(nodes) {
            const map = new Map();
            const roots = [];
            for (const node of nodes) {
                map.set(node.sysNodeIdx, { ...node, children: [] });
            }
            for (const node of nodes) {
                const item = map.get(node.sysNodeIdx);
                const parentIdx = node.parentIdx != null ? Number(node.parentIdx) : null;
                if (parentIdx != null && map.has(parentIdx)) {
                    map.get(parentIdx).children.push(item);
                } else {
                    roots.push(item);
                }
            }
            return roots;
        },
        sortChildren(children) {
            return [...children].sort((a, b) => {
                const oa = ROLE_ORDER[a.role] ?? 5;
                const ob = ROLE_ORDER[b.role] ?? 5;
                if (oa !== ob) return oa - ob;
                return String(a.sysNodeName || "").localeCompare(String(b.sysNodeName || ""), "ko");
            });
        },
        flattenTree(nodes, depth = 0) {
            const result = [];
            for (const node of this.sortChildren(nodes)) {
                result.push({ ...node, displayDepth: depth });
                if (node.children?.length) {
                    result.push(...this.flattenTree(node.children, depth + 1));
                }
            }
            return result;
        },
    },
};
</script>

<style scoped>
.stp {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    gap: 8px;
}

.stp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
}

.stp-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
}
.stp-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: #4b5363;
}
.stp-legend-cnt {
    font-variant-numeric: tabular-nums;
}

.stp-dot {
    display: inline-block;
    flex-shrink: 0;
    border-radius: 999px;
}
.stp-legend .stp-dot {
    width: 8px;
    height: 8px;
}

.stp-dot--SITE_ROOT {
    background: #1a1f27;
}
.stp-dot--BUILDING {
    background: #2d5fc7;
}
.stp-dot--COMMON_AREA {
    background: #a06d1a;
}
.stp-dot--HOUSEHOLD {
    background: #1e7e52;
}
.stp-dot--ENERGY_EQUIP {
    background: #9aa3b2;
    border-radius: 2px;
}

.stp-seg {
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    background: #eef0f5;
    border-radius: 9px;
    flex-shrink: 0;
}
.stp-seg-btn {
    height: 26px;
    padding: 0 12px;
    border-radius: 7px;
    border: none;
    background: transparent;
    font-size: 11.5px;
    font-weight: 600;
    color: #7e8798;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
    transition: background 0.12s, color 0.12s, box-shadow 0.12s;
}
.stp-seg-btn--active {
    background: #fff;
    color: #1a1f27;
    box-shadow: 0 1px 2px rgba(40, 36, 30, 0.14);
}

.stp-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 13px;
    background: #fbf5e0;
    border: 1px solid #e8d9a8;
    border-radius: 10px;
    font-size: 12.5px;
    color: #a06d1a;
    flex-shrink: 0;
}

.stp-tree {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.stp-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 30px;
    padding-top: 3px;
    padding-bottom: 3px;
    padding-right: 8px;
    border-radius: 8px;
    transition: background 0.1s;
}
.stp-row:hover {
    background: #f6f7fa;
}

.stp-row--SITE_ROOT {
    min-height: 34px;
}
.stp-row--ENERGY_EQUIP {
    min-height: 25px;
    opacity: 0.85;
}

.stp-row-dot {
    width: 8px;
    height: 8px;
}
.stp-row--HOUSEHOLD .stp-row-dot {
    width: 7px;
    height: 7px;
}
.stp-row--ENERGY_EQUIP .stp-row-dot {
    width: 5px;
    height: 5px;
}

.stp-row-name {
    font-size: 12.5px;
    font-weight: 600;
    color: #1a1f27;
}
.stp-row--SITE_ROOT .stp-row-name {
    font-size: 13.5px;
    font-weight: 700;
}
.stp-row--BUILDING .stp-row-name {
    font-size: 13px;
    font-weight: 700;
}
.stp-row--ENERGY_EQUIP .stp-row-name {
    font-size: 12px;
    font-weight: 500;
    color: #4b5363;
}

.stp-role-pill {
    font-size: 10px;
    font-weight: 700;
    border-radius: 5px;
    padding: 1px 7px;
    flex-shrink: 0;
}
.stp-role-pill--SITE_ROOT {
    background: #1a1f27;
    color: #fff;
}
.stp-role-pill--BUILDING {
    background: #eef3fc;
    color: #234e9d;
    border: 1px solid #cdd9f0;
}
.stp-role-pill--COMMON_AREA {
    background: #fbf5e0;
    color: #a06d1a;
    border: 1px solid #e8d9a8;
}

.stp-spacer {
    flex: 1;
}
.stp-branch-summary {
    font-size: 11px;
    color: #7e8798;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    flex-shrink: 0;
}

.stp-chips {
    display: inline-flex;
    gap: 5px;
    margin-left: 3px;
    flex-wrap: wrap;
}
.stp-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 20px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 10.5px;
    font-weight: 600;
    flex-shrink: 0;
}
.stp-chip-dot {
    width: 5px;
    height: 5px;
    border-radius: 999px;
    flex-shrink: 0;
}

.stp-loading {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    font-size: 13px;
    color: #7e8798;
}
.stp-empty {
    padding: 24px 16px;
    text-align: center;
    color: #7e8798;
    font-size: 13px;
}
</style>
